<template>
  <div class="pd-root">

    <!-- ── PAGE HEADER ── -->
    <div class="pd-header">
      <div>
        <h1 class="pd-greeting">{{ greeting }}, {{ firstName }} 👋</h1>
        <p class="pd-subgreeting">{{ formattedDate }} · {{ childCountLabel }}</p>
      </div>
      <div class="pd-add-wrap">
        <button
          class="pd-add-btn"
          :class="{ 'pd-add-btn-disabled': atChildLimit }"
          :disabled="atChildLimit"
          @click="addChild"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          Add Child
        </button>
        <div v-if="atChildLimit" class="pd-limit-note">
          {{ childLimitNote }}
          <RouterLink v-if="!isTopPlan" to="/parent/subscription" class="pd-limit-upgrade">Upgrade →</RouterLink>
        </div>
      </div>
    </div>

    <!-- ── PLAN STATUS CARD ── -->
    <div v-if="!isCancelling && !hasScheduledDowngrade" class="pd-plan-card">
      <div class="pd-plan-card-left">
        <div class="pd-plan-icon">{{ planIcon }}</div>
        <div>
          <div class="pd-plan-name">{{ planDisplayName }} Plan</div>
          <div class="pd-plan-detail">
            <span v-if="isCancelling">Access until {{ formattedCancelDate }}</span>
            <span v-else-if="formattedCancelDate">Renews {{ formattedCancelDate }}</span>
            <span v-else>Free forever — no card required</span>
          </div>
        </div>
      </div>
      <div class="pd-plan-card-right">
        <span class="pd-plan-status-badge" :class="planStatusBadgeClass">{{ planStatusLabel }}</span>
        <RouterLink to="/parent/subscription" class="pd-plan-manage-link">Manage →</RouterLink>
      </div>
    </div>

    <!-- ── FREE-PLAN UPGRADE BANNER ── -->
    <div v-if="isFreePlan && !isCancelling" class="pd-upgrade-banner">
      <div class="pd-upgrade-left">
        <div class="pd-upgrade-icon">⭐</div>
        <div>
          <div class="pd-upgrade-title">Unlock the full 11+ experience</div>
          <div class="pd-upgrade-desc">Upgrade to Pro for revision mode, detailed progress reports, diagnostics and more.</div>
        </div>
      </div>
      <RouterLink to="/parent/subscription" class="pd-upgrade-btn">Upgrade to Pro →</RouterLink>
    </div>

    <!-- ── SCHEDULED DOWNGRADE BANNER ── -->
    <div v-if="hasScheduledDowngrade" class="pd-downgrade-banner">
      <div class="pd-downgrade-left">
        <div class="pd-downgrade-icon">📉</div>
        <div>
          <div class="pd-downgrade-title">Plan downgrade scheduled to {{ scheduledDowngradePlanLabel }}</div>
          <div class="pd-downgrade-desc">
            Your plan changes to {{ scheduledDowngradePlanLabel }} on <strong>{{ scheduledDowngradeDate }}</strong>.
            Children marked for archival will be removed on that date.
          </div>
        </div>
      </div>
      <RouterLink to="/parent/subscription" class="pd-downgrade-btn">Manage →</RouterLink>
    </div>

    <!-- ── CANCELLATION REMINDER BANNER ── -->
    <div v-if="isCancelling" class="pd-cancelling-banner">
      <div class="pd-cancelling-left">
        <div class="pd-cancelling-icon">⏳</div>
        <div>
          <div class="pd-cancelling-title">{{ cancelllingPlanLabel }} plan cancelling</div>
          <div class="pd-cancelling-desc">Access ends <strong>{{ formattedCancelDate }}</strong>.</div>
        </div>
      </div>
      <RouterLink to="/parent/subscription" class="pd-reactivate-btn">Reactivate plan →</RouterLink>
    </div>

    <!-- ── SUMMARY STRIP (only if any children) ── -->
    <div v-if="children.length" class="pd-summary">
      <div class="pd-stat-card">
        <div class="pd-stat-label">Best Streak</div>
        <div class="pd-stat-value">🔥 {{ bestStreak.count }}</div>
        <div class="pd-stat-hint">{{ bestStreak.name ? `${bestStreak.name} · ` : '' }}days in a row</div>
      </div>
      <div class="pd-stat-card">
        <div class="pd-stat-label">Sessions This Week</div>
        <div class="pd-stat-value">{{ sessionsThisWeek }}</div>
        <div class="pd-stat-hint">across all children</div>
      </div>
      <div class="pd-stat-card">
        <div class="pd-stat-label">Combined Accuracy</div>
        <div class="pd-stat-value">{{ combinedAccuracy }}%</div>
        <div class="pd-stat-hint">all topics answered</div>
      </div>
    </div>

    <!-- ── CHILDREN ── -->
    <template v-if="children.length">
      <div class="pd-section-label">Your Children</div>

      <div
        v-for="child in children"
        :key="child.id"
        class="pd-child-card"
        :class="{ 'pd-child-card-archiving': child.scheduledArchiveAt }"
      >
        <!-- Avatar -->
        <div class="pd-avatar-wrap">
          <MTAvatar :studentId="child.id" size="lg" class="pd-avatar" />
        </div>

        <!-- Info block -->
        <div class="pd-child-info">

          <!-- Scheduled archival warning -->
          <div v-if="child.scheduledArchiveAt" class="pd-archive-warning">
            <span class="pd-archive-warning-icon">⏳</span>
            <span>Scheduled for archival on <strong>{{ formatArchiveDate(child.scheduledArchiveAt) }}</strong></span>
            <RouterLink to="/parent/subscription" class="pd-archive-warning-link">Manage →</RouterLink>
          </div>

          <!-- Inline name editor -->
          <div class="pd-name-row">
            <template v-if="editingId === child.id">
              <input
                class="pd-name-input"
                v-model="editingName"
                :ref="el => el && (el.focus(), el.select())"
                @keydown.enter.prevent="commitRename(child.id)"
                @keydown.escape="cancelRename"
                @blur="commitRename(child.id)"
                maxlength="40"
              />
            </template>
            <template v-else>
              <span class="pd-child-name">{{ child.name }}</span>
              <button class="pd-rename-btn" @click="startRename(child)" title="Rename">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                </svg>
              </button>
            </template>
          </div>
          <div class="pd-child-meta">
            <span class="pd-track-badge">{{ trackLabel(child.track) }}</span>
            <span class="pd-last-active" :class="lastActiveClass(child.id)">
              {{ lastActive(child.id) }}
            </span>
          </div>

          <!-- Streak chip -->
          <div v-if="childStreak(child.id) > 0" class="pd-streak-chip">
            🔥 {{ childStreak(child.id) }}-day streak
          </div>

          <!-- Progress bar -->
          <div class="pd-progress-row">
            <div class="pd-progress-label">
              <span>Curriculum progress</span>
              <span>{{ overallProgress(child.id) }}% complete</span>
            </div>
            <div class="pd-progress-bar">
              <div class="pd-progress-fill" :style="{ width: overallProgress(child.id) + '%' }"></div>
            </div>
          </div>

          <!-- Stats row -->
          <div class="pd-stats-row">
            <div class="pd-mini-stat">
              <div class="pd-mini-val">{{ topicsSecure(child.id) }}</div>
              <div class="pd-mini-lbl">Topics Secure+</div>
            </div>
            <div class="pd-mini-stat">
              <div class="pd-mini-val">{{ lessonsCompleted(child.id) }}</div>
              <div class="pd-mini-lbl">Lessons done</div>
            </div>
          </div>

        </div>

        <!-- Actions -->
        <div class="pd-actions">
          <button class="pd-btn-primary" @click="openStudentView(child.id)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <polygon points="5 3 19 12 5 21 5 3"/>
            </svg>
            Open Student View
          </button>
          <button class="pd-btn-secondary" @click="goToMetrics(child.id)">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
              <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
            </svg>
            View Progress
          </button>
        </div>
      </div>
    </template>

    <!-- ── EMPTY STATE ── -->
    <!-- Loading spinner — shown while children API call is in flight -->
    <div v-else-if="childrenLoading" class="pd-loading">
      <div class="pd-spinner"></div>
      <p class="pd-loading-text">Loading…</p>
    </div>

    <!-- Empty state — only shown once loading is complete with no children -->
    <div v-else class="pd-empty">
      <div class="pd-empty-icon">👨‍👩‍👧‍👦</div>
      <h2 class="pd-empty-title">Add your first child to get started</h2>
      <p class="pd-empty-sub">
        Set up a personalised 11+ learning journey, track progress and run skills checks — all in one place.
      </p>
      <button
        class="pd-add-btn"
        :class="{ 'pd-add-btn-disabled': atChildLimit }"
        :disabled="atChildLimit"
        style="margin-top: 24px;"
        @click="addChild"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
          <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
        </svg>
        Add Your First Child
      </button>
    </div>

  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { useRouter } from "vue-router";

import MTAvatar from "@/components/ui/MTAvatar.vue";

import { useStudentStore } from "@/stores/studentStore";
import { useProgressStore } from "@/stores/progressStore";
import { useCurriculumStore } from "@/stores/curriculumStore";
import { useUserStore } from "@/stores/userStore";
import { usePlanLimits } from "@/composables/usePlanLimits";
import { useAnalytics } from "@/composables/useAnalytics";
import { PLAN_ICONS } from "@/services/planService";

const router = useRouter();
const studentStore = useStudentStore();
const progressStore = useProgressStore();
const curriculumStore = useCurriculumStore();
const userStore = useUserStore();
const { track } = useAnalytics();

const children          = computed(() => studentStore.students);
const childrenLoading   = computed(() => studentStore.loading);
const isFreePlan        = computed(() => (userStore.effectivePlan || 'free') === 'free');

// Plan status card
const PLAN_LABELS = { free: 'Free', pro: 'Pro', pro_plus: 'Pro Plus', family: 'Family' };
const planDisplayName = computed(() => PLAN_LABELS[userStore.effectivePlan || 'free'] || 'Free');
const planIcon        = computed(() => PLAN_ICONS[userStore.effectivePlan || 'free'] ?? '🌱');
const planStatusLabel = computed(() => {
  if (isCancelling.value) return 'Cancelling';
  const status = userStore.session?.subscriptionStatus;
  if (status === 'trial') return 'Trial';
  return 'Active';
});
const planStatusBadgeClass = computed(() => ({
  'pd-plan-badge-active':    !isCancelling.value,
  'pd-plan-badge-cancelling': isCancelling.value,
}));

// Scheduled downgrade banner
const hasScheduledDowngrade = computed(() => !!userStore.session?.scheduledDowngrade);
const scheduledDowngradeDate = computed(() => {
  const d = userStore.session?.scheduledDowngrade?.effectiveAt;
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
});
const scheduledDowngradePlanLabel = computed(() => {
  const p = userStore.session?.scheduledDowngrade?.plan;
  return PLAN_LABELS[p] || p || 'lower';
});

// Cancellation banner: show when subscription is cancelled but still active (renewsAt in future)
const isCancelling = computed(() =>
  userStore.session?.subscriptionStatus === 'cancelled' &&
  !isFreePlan.value
);
const cancelllingPlanLabel = computed(() => {
  const p = userStore.effectivePlan || 'free';
  return { free: 'Free', pro: 'Pro', pro_plus: 'Pro Plus', family: 'Family' }[p] || p;
});
const formattedCancelDate = computed(() => {
  const d = userStore.session?.subscriptionRenewsAt;
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
});

// Plan limits — always derived from the live effectivePlan so they update
// immediately after an upgrade without needing a loadChildren() refresh.
const planLimits = usePlanLimits();
const atChildLimit  = computed(() => children.value.length >= planLimits.getMaxChildren());
const isTopPlan     = computed(() => (userStore.effectivePlan || 'free') === 'family');
const childLimitNote = computed(() => {
  const current     = studentStore.childLimit?.current ?? children.value.length;
  const max         = planLimits.getMaxChildren();
  const planName    = userStore.effectivePlan || 'free';
  return `${current}/${max} children on ${planName} plan.`;
});

onMounted(async () => {
  await curriculumStore.loadTrack("eplus_core");
  planLimits.loadPlans(); // non-blocking: updates limits reactively once resolved
  // Load progress for all children in parallel
  await Promise.all(children.value.map(c => progressStore.loadProgress(c.id)));
});

// ── Inline rename ─────────────────────────────────────────────
const editingId = ref(null);
const editingName = ref('');

function startRename(child) {
  editingId.value = child.id;
  editingName.value = child.name;
}

function commitRename(childId) {
  const trimmed = editingName.value.trim();
  if (trimmed) studentStore.renameChild(childId, trimmed);
  editingId.value = null;
}

function cancelRename() {
  editingId.value = null;
}

// ── Greeting ──────────────────────────────────────────────────
const greeting = computed(() => {
  const h = new Date().getHours();
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
});

const firstName = computed(() => {
  const name = userStore.session?.name || 'there';
  return name.split(' ')[0];
});

const formattedDate = computed(() => {
  return new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
});

const childCountLabel = computed(() => {
  const n = children.value.length;
  if (n === 0) return 'No children yet';
  return n === 1 ? '1 child enrolled' : `${n} children enrolled`;
});

// ── Summary strip ─────────────────────────────────────────────
const bestStreak = computed(() => {
  if (!children.value.length) return { count: 0, name: '' };
  const best = children.value.reduce((top, c) => {
    const s = childStreak(c.id);
    return s > top.count ? { count: s, name: c.name.split(' ')[0] } : top;
  }, { count: 0, name: '' });
  return best;
});

const sessionsThisWeek = computed(() => {
  const weekStart = startOfWeekMs();
  return children.value.reduce((total, c) => {
    const log = progressStore.activityLog[c.id] || [];
    return total + log.filter(e => (e.timestamp || 0) >= weekStart).length;
  }, 0);
});

const combinedAccuracy = computed(() => {
  if (!children.value.length) return 0;
  const vals = children.value.flatMap(c => {
    const prog = progressStore.getChildProgress(c.id) || {};
    return Object.values(prog).map(v => v.accuracy ?? 0).filter(v => v > 0);
  });
  if (!vals.length) return 0;
  return Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100);
});

function startOfWeekMs() {
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Mon start
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
}

// ── Per-child helpers ─────────────────────────────────────────
function childStreak(childId) {
  return progressStore.dailyStreak?.[childId]?.count ?? 0;
}

function getChildProg(childId) {
  return progressStore.getChildProgress(childId) || {};
}

function topicsSecure(childId) {
  const prog = getChildProg(childId);
  const SECURE_BANDS = new Set(['secure', 'confident', 'mastery']);
  return Object.keys(prog).filter(sgId => {
    const band = progressStore.getSectionGroupBand(childId, sgId);
    return SECURE_BANDS.has(band);
  }).length;
}

function overallProgress(childId) {
  try {
    const child = studentStore.students.find(s => s.id === childId);
    if (!child || !curriculumStore.modules?.length) return 0;

    let total = 0, count = 0;
    const childProg = getChildProg(childId);

    for (const mod of curriculumStore.modules) {
      for (const unit of mod.units) {
        for (const lesson of unit.lessons) {
          const subs = lesson.subsections || [];
          if (!subs.length) continue;
          const sum = subs.reduce((acc, sub) => acc + (childProg[sub.content]?.accuracy ?? 0), 0);
          total += sum / subs.length;
          count++;
        }
      }
    }
    return count > 0 ? Math.round((total / count) * 100) : 0;
  } catch {
    return 0;
  }
}

function lessonsCompleted(childId) {
  try {
    if (!curriculumStore.modules?.length) return 0;
    const childProg = getChildProg(childId);
    const SECURE_THRESHOLD = 0.65;
    let done = 0;
    for (const mod of curriculumStore.modules) {
      for (const unit of mod.units) {
        for (const lesson of unit.lessons) {
          const subs = lesson.subsections || [];
          if (!subs.length) continue;
          const allSecure = subs.every(sub => (childProg[sub.content]?.accuracy ?? 0) >= SECURE_THRESHOLD);
          if (allSecure) done++;
        }
      }
    }
    return done;
  } catch {
    return 0;
  }
}

function lastActive(childId) {
  const log = progressStore.activityLog?.[childId] || [];
  if (!log.length) return 'No activity yet';
  const latest = Math.max(...log.map(e => e.timestamp || 0));
  if (!latest) return 'No activity yet';
  const diffMs = Date.now() - latest;
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return 'Active today';
  if (diffDays === 1) return 'Active yesterday';
  return `Active ${diffDays} days ago`;
}

function lastActiveClass(childId) {
  const text = lastActive(childId);
  if (text === 'Active today') return 'pd-active-today';
  if (text === 'Active yesterday') return 'pd-active-yesterday';
  if (text === 'No activity yet') return 'pd-active-never';
  return 'pd-active-ago';
}

// ── Scheduled archive date formatting ─────────────────────────
function formatArchiveDate(dateVal) {
  if (!dateVal) return '';
  return new Date(dateVal).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

// ── Track badge ───────────────────────────────────────────────
function trackLabel(track) {
  return { eplus_core: '11+ Core', core: '11+ Core', intensive: '11+ Intensive', mastery: '11+ Mastery' }[track] || '11+ Core';
}

// ── Actions ───────────────────────────────────────────────────
function openStudentView(childId) {
  router.push(`/student/${childId}/dashboard`);
}

function goToMetrics(childId) {
  track('child_progress_viewed', { child_id: childId });
  router.push(`/parent/${childId}/metrics`);
}

function editChild(childId) {
  router.push(`/parent/children/${childId}/edit`);
}

function addChild() {
  router.push('/onboarding');
}
</script>

<style scoped>
/* ── Root ── */
.pd-root {
  max-width: 72rem;
}

/* ── Header ── */
.pd-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 28px;
  gap: 16px;
  flex-wrap: wrap;
}
.pd-greeting {
  font-size: 26px;
  font-weight: 800;
  color: #1A2332;
  line-height: 1.2;
}
.pd-subgreeting {
  font-size: 14px;
  color: #64748b;
  margin-top: 5px;
}

.pd-add-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #F59E0B;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 11px 20px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(245,158,11,0.35);
  transition: all 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}
.pd-add-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 6px 20px rgba(245,158,11,0.45);
}

.pd-add-btn-disabled {
  background: #E2E8F0;
  color: #94A3B8;
  box-shadow: none;
  cursor: not-allowed;
}

.pd-add-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  flex-shrink: 0;
}

.pd-limit-note {
  font-size: 11px;
  color: #94A3B8;
  display: flex;
  align-items: center;
  gap: 6px;
}

.pd-limit-upgrade {
  color: #F59E0B;
  font-weight: 700;
  text-decoration: none;
  font-size: 11px;
}
.pd-limit-upgrade:hover { text-decoration: underline; }

/* ── Summary strip ── */
.pd-summary {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-bottom: 28px;
}
.pd-stat-card {
  background: white;
  border-radius: 12px;
  padding: 18px 20px;
  border: 1px solid #EEE;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04), 0 4px 12px rgba(0,0,0,0.03);
}
.pd-stat-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.pd-stat-value {
  font-size: 28px;
  font-weight: 800;
  color: #1A2332;
  margin: 6px 0 4px;
  line-height: 1;
}
.pd-stat-hint {
  font-size: 12px;
  color: #94a3b8;
}

/* ── Section label ── */
.pd-section-label {
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  margin-bottom: 14px;
}

/* ── Child card ── */
.pd-child-card {
  background: white;
  border-radius: 16px;
  border: 1px solid #EEE;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04), 0 6px 20px rgba(0,0,0,0.04);
  padding: 24px;
  margin-bottom: 16px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.pd-avatar-wrap {
  flex-shrink: 0;
}

.pd-child-info {
  flex: 1;
  min-width: 0;
}

.pd-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 32px;
}
.pd-child-name {
  font-size: 20px;
  font-weight: 800;
  color: #1A2332;
  line-height: 1.2;
}
.pd-rename-btn {
  background: transparent;
  border: none;
  padding: 4px;
  border-radius: 6px;
  color: #CBD5E1;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.15s;
  opacity: 0;
}
.pd-child-card:hover .pd-rename-btn {
  opacity: 1;
}
.pd-rename-btn:hover {
  background: #F1F5F9;
  color: #64748b;
}
.pd-name-input {
  font-size: 20px;
  font-weight: 800;
  color: #1A2332;
  border: none;
  border-bottom: 2px solid #F59E0B;
  background: transparent;
  outline: none;
  padding: 0 2px;
  width: 220px;
  max-width: 100%;
  line-height: 1.2;
}

.pd-child-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 5px;
  flex-wrap: wrap;
}
.pd-track-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 20px;
  background: #DBEAFE;
  color: #2563EB;
}
.pd-last-active {
  font-size: 12px;
  font-weight: 600;
}
.pd-active-today { color: #15803d; }
.pd-active-yesterday { color: #92400e; }
.pd-active-ago { color: #94a3b8; }
.pd-active-never { color: #cbd5e1; }

.pd-streak-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #FEF3C7;
  border: 1px solid #FDE68A;
  border-radius: 20px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 700;
  color: #92400E;
  margin-top: 8px;
}

/* Progress bar */
.pd-progress-row {
  margin-top: 14px;
}
.pd-progress-label {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 6px;
}
.pd-progress-bar {
  height: 8px;
  border-radius: 99px;
  background: #F1F5F9;
  overflow: hidden;
}
.pd-progress-fill {
  height: 100%;
  border-radius: 99px;
  background: linear-gradient(90deg, #F59E0B, #EF4444);
  transition: width 0.4s ease;
}

/* Stats row */
.pd-stats-row {
  display: flex;
  gap: 24px;
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #F1F5F9;
  flex-wrap: wrap;
}
.pd-mini-stat {}
.pd-mini-val {
  font-size: 20px;
  font-weight: 800;
  color: #1A2332;
  line-height: 1;
}
.pd-mini-lbl {
  font-size: 11px;
  color: #94a3b8;
  font-weight: 500;
  margin-top: 3px;
}


/* Actions column */
.pd-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 160px;
  flex-shrink: 0;
}

.pd-btn-primary {
  padding: 11px 16px;
  border-radius: 10px;
  border: none;
  background: #1A2332;
  color: white;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: all 0.15s;
  width: 100%;
}
.pd-btn-primary:hover {
  background: #2d3748;
}

.pd-btn-secondary {
  padding: 11px 16px;
  border-radius: 10px;
  border: 2px solid #F59E0B;
  background: transparent;
  color: #B45309;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: all 0.15s;
  width: 100%;
}
.pd-btn-secondary:hover {
  background: rgba(245,158,11,0.07);
}

.pd-btn-ghost {
  padding: 10px 16px;
  border-radius: 10px;
  border: 1px solid #E2E8F0;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: all 0.15s;
  width: 100%;
}
.pd-btn-ghost:hover {
  border-color: #CBD5E1;
  color: #475569;
  background: #F8FAFC;
}

/* ── Empty state ── */
.pd-empty {
  background: white;
  border-radius: 16px;
  border: 2px dashed #E2E8F0;
  padding: 56px 32px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
}
.pd-empty-icon {
  font-size: 52px;
  margin-bottom: 16px;
}
.pd-empty-title {
  font-size: 20px;
  font-weight: 800;
  color: #1A2332;
  margin-bottom: 10px;
}
.pd-empty-sub {
  font-size: 14px;
  color: #64748b;
  max-width: 420px;
  line-height: 1.6;
}

/* ── Responsive ── */
@media (max-width: 700px) {
  .pd-summary {
    grid-template-columns: 1fr;
  }
  .pd-child-card {
    flex-direction: column;
  }
  .pd-actions {
    min-width: 0;
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
  }
  .pd-actions button {
    flex: 1;
    min-width: 120px;
  }
  .pd-stats-row {
    gap: 16px;
  }
}
/* ── Loading spinner ── */
.pd-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  gap: 16px;
}
.pd-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #e2e8f0;
  border-top-color: #F59E0B;
  border-radius: 50%;
  animation: pd-spin 0.7s linear infinite;
}
@keyframes pd-spin {
  to { transform: rotate(360deg); }
}
.pd-loading-text {
  font-size: 14px;
  color: #94a3b8;
  margin: 0;
}

/* ── Scheduled-archival card state ── */
.pd-child-card-archiving {
  border-color: #fed7aa;
  background: #fffbf5;
}

.pd-archive-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff7ed;
  border: 1px solid #fed7aa;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #92400e;
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.pd-archive-warning-icon { font-size: 14px; flex-shrink: 0; }
.pd-archive-warning-link {
  margin-left: auto;
  font-size: 11px;
  font-weight: 700;
  color: #d97706;
  text-decoration: none;
  white-space: nowrap;
}
.pd-archive-warning-link:hover { text-decoration: underline; }

/* ── Plan status card ── */
.pd-plan-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 16px 22px;
  margin-bottom: 20px;
  flex-wrap: wrap;
}
.pd-plan-card-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pd-plan-icon {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  flex-shrink: 0;
}
.pd-plan-name {
  font-size: 15px;
  font-weight: 700;
  color: #1A2332;
}
.pd-plan-detail {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}
.pd-plan-card-right {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.pd-plan-status-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 4px 11px;
  border-radius: 20px;
  letter-spacing: 0.03em;
}
.pd-plan-badge-active    { background: #dcfce7; color: #16a34a; }
.pd-plan-badge-cancelling { background: #fef3c7; color: #92400e; }
.pd-plan-manage-link {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  text-decoration: none;
  white-space: nowrap;
}
.pd-plan-manage-link:hover { color: #1A2332; }

/* ── Upgrade banner (free plan only) ── */
.pd-upgrade-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: linear-gradient(135deg, #1A2332 0%, #2d3f57 100%);
  border-radius: 14px;
  padding: 18px 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.pd-upgrade-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pd-upgrade-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(245,158,11,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.pd-upgrade-title {
  font-size: 14px;
  font-weight: 700;
  color: white;
  margin-bottom: 3px;
}
.pd-upgrade-desc {
  font-size: 12px;
  color: rgba(255,255,255,0.6);
  max-width: 420px;
}
.pd-upgrade-btn {
  flex-shrink: 0;
  background: #F59E0B;
  color: #1A2332;
  font-size: 13px;
  font-weight: 800;
  padding: 10px 20px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s;
  white-space: nowrap;
}
.pd-upgrade-btn:hover { background: #fbbf24; }

/* ── Scheduled downgrade banner ── */
.pd-downgrade-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #f5f3ff;
  border: 1.5px solid #c4b5fd;
  border-radius: 14px;
  padding: 18px 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.pd-downgrade-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pd-downgrade-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #ede9fe;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.pd-downgrade-title {
  font-size: 14px;
  font-weight: 700;
  color: #4c1d95;
  margin-bottom: 3px;
}
.pd-downgrade-desc {
  font-size: 12px;
  color: #5b21b6;
  max-width: 420px;
  line-height: 1.5;
}
.pd-downgrade-btn {
  flex-shrink: 0;
  background: #7c3aed;
  color: white;
  font-size: 13px;
  font-weight: 800;
  padding: 10px 20px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s;
  white-space: nowrap;
}
.pd-downgrade-btn:hover { background: #6d28d9; }

/* ── Cancellation reminder banner ── */
.pd-cancelling-banner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  background: #fffbeb;
  border: 1.5px solid #fcd34d;
  border-radius: 14px;
  padding: 18px 24px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}
.pd-cancelling-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pd-cancelling-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: #fef3c7;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}
.pd-cancelling-title {
  font-size: 14px;
  font-weight: 700;
  color: #92400e;
  margin-bottom: 3px;
}
.pd-cancelling-desc {
  font-size: 12px;
  color: #b45309;
  max-width: 420px;
  line-height: 1.5;
}
.pd-reactivate-btn {
  flex-shrink: 0;
  background: #d97706;
  color: white;
  font-size: 13px;
  font-weight: 800;
  padding: 10px 20px;
  border-radius: 10px;
  text-decoration: none;
  transition: background 0.15s;
  white-space: nowrap;
}
.pd-reactivate-btn:hover { background: #b45309; }
</style>
